import { escposJob, type ScaleProtocol } from '@makila/pos-core';
import { printHtml } from '../receipt';
import {
  findBluetooth, findSerial, findUsb, nativeBridge, toBase64, writeBluetooth, writeSerial, writeUsb,
  type BtDeviceLike, type DeviceRef, type SerialPortLike, type UsbDeviceLike,
} from './devices';
import { htmlToBitmap } from './render';

/**
 * Equipamento desta caixa (fica no aparelho — cada caixa tem o seu).
 * - printer 'system': a impressão do sistema (o comportamento de sempre).
 * - 'usb' | 'serial' | 'bluetooth' | 'native': ESC/POS directo, com gaveta.
 * Se a ligação directa falhar, o talão sai pela impressão do sistema: nunca
 * se fica sem talão por causa do cabo.
 */
export interface HwConfig {
  printer: { kind: 'system' | 'usb' | 'serial' | 'bluetooth' | 'native'; paper: 58 | 80; baudRate: number; drawerOnCash: boolean; ref?: DeviceRef };
  scale: { kind: 'none' | 'serial' | 'native'; protocol: ScaleProtocol; baudRate: number; ref?: DeviceRef };
}
const KEY = 'makila-pos-hw';
export const DEFAULT_HW: HwConfig = { printer: { kind: 'system', paper: 80, baudRate: 9600, drawerOnCash: true }, scale: { kind: 'none', protocol: 'stream', baudRate: 9600 } };

export function loadHw(): HwConfig {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<HwConfig> | null;
    return { printer: { ...DEFAULT_HW.printer, ...v?.printer }, scale: { ...DEFAULT_HW.scale, ...v?.scale } };
  } catch { return DEFAULT_HW; }
}
export function saveHw(c: HwConfig) {
  try { localStorage.setItem(KEY, JSON.stringify(c)); } catch { /* sem armazenamento: vale só nesta sessão */ }
  live.printer = undefined;
  window.dispatchEvent(new Event('makila-hw'));
}

/** Aparelho aberto nesta sessão (escolhido agora ou reencontrado). */
const live: { printer?: SerialPortLike | UsbDeviceLike | BtDeviceLike } = {};
export const setLivePrinter = (d: SerialPortLike | UsbDeviceLike | BtDeviceLike) => { live.printer = d; };

async function sendBytes(c: HwConfig['printer'], bytes: Uint8Array) {
  const ref = c.ref;
  if (!ref) throw new Error('Impressora por ligar');
  if (c.kind === 'native') { const n = nativeBridge(); if (!n) throw new Error('Aplicação Android não detectada'); return n.print(ref.nativeId!, toBase64(bytes)); }
  if (c.kind === 'serial') {
    const p = (live.printer as SerialPortLike | undefined) ?? await findSerial(ref);
    if (!p) throw new Error('Porta série não encontrada (ligue o cabo ou volte a escolher a impressora)');
    live.printer = p; return writeSerial(p, bytes, c.baudRate);
  }
  if (c.kind === 'usb') {
    const d = (live.printer as UsbDeviceLike | undefined) ?? await findUsb(ref);
    if (!d) throw new Error('Impressora USB não encontrada (ligue o cabo ou volte a escolher a impressora)');
    live.printer = d; return writeUsb(d, bytes);
  }
  const d = (live.printer as BtDeviceLike | undefined) ?? await findBluetooth(ref);
  if (!d) throw new Error('Impressora Bluetooth por ligar nesta sessão (Equipamento → Ligar)');
  live.printer = d; return writeBluetooth(d, bytes);
}

/** Último erro da impressora directa (o ecrã do equipamento mostra-o). */
export let lastPrinterError: string | null = null;

/**
 * Talão, Z, consulta ou talão de cozinha (papel térmico). Com impressora
 * directa: desenha, envia e — se pedido — abre a gaveta. Sem ela, ou se falhar,
 * a impressão do sistema (a gaveta abre então pelo controlador, se configurado).
 */
export async function printThermal(html: string, opts: { drawer?: boolean } = {}): Promise<'direct' | 'system'> {
  const c = loadHw().printer;
  if (c.kind !== 'system') {
    try {
      const img = await htmlToBitmap(html, c.paper);
      await sendBytes(c, escposJob(img, { drawer: !!opts.drawer && c.drawerOnCash }));
      lastPrinterError = null;
      return 'direct';
    } catch (e) {
      lastPrinterError = (e as Error).message;
      console.warn('[Makila POS] impressora directa falhou; a usar a impressão do sistema:', e);
    }
  }
  await printHtml(html);
  return 'system';
}

/** Abre a gaveta sem talão (abertura registada, sangria, reforço). Só com impressora directa. */
export async function openDrawer(): Promise<boolean> {
  const c = loadHw().printer;
  if (c.kind === 'system') return false;
  try { await sendBytes(c, escposJob(null, { drawer: true })); lastPrinterError = null; return true; } catch (e) { lastPrinterError = (e as Error).message; return false; }
}

/** Página de teste: confirma a ligação, a largura do papel e os acentos. */
export const testPageHtml = (paper: 58 | 80) => `<!doctype html><html lang="pt"><head><meta charset="utf-8"><style>
  body { margin: 0; padding: 3mm 0; width: ${paper}mm; font: 12px/1.35 ui-monospace, "Courier New", monospace; color: #000; } h1 { font-size: 18px; text-align: center; margin: 0 0 4px; }
  .bar { height: 6px; background: #000; margin: 4px 0; } .r { text-align: right; }
</style></head><body><h1>Makila POS</h1><div class="bar"></div>
  <div>Teste de impressão — papel ${paper} mm</div><div>Acentuação: ação, côco, maçã, pão, Moçambique</div><div class="r">1 234,56 MT</div>
  <div class="bar"></div><div>${new Date().toLocaleString('pt-PT', { timeZone: 'Africa/Maputo' })}</div></body></html>`;
